import React, { useRef, useState } from "react";
import { Download, Upload, Database } from "lucide-react";
import { api } from "@/api";
import { toast } from "sonner";

/**
 * One-click Export / Import panel for the Tools → Backup subtab.
 * Export → downloads a timestamped JSON of everything.
 * Import → opens a JSON file picker and merges the file into the current DB.
 */
export const BackupPanel = ({ onDataChanged }) => {
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const doExport = async () => {
        setBusy(true);
        try {
            const bundle = await api.exportBackup();
            const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
            a.href = url;
            a.download = `meyetasks-backup-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch (e) {
            console.error(e);
            toast.error("Export failed");
        } finally {
            setBusy(false);
        }
    };

    const [report, setReport] = useState(null); // dry-run result awaiting "Apply Import"
    const bundleRef = useRef(null);

    const onFilePicked = async (e) => {
        const file = e.target.files?.[0];
        if (fileRef.current) fileRef.current.value = "";
        if (!file) return;
        setBusy(true);
        const loadingId = toast.loading(`Checking ${file.name}…`);
        try {
            const raw = await file.text();
            const parsed = JSON.parse(raw);
            bundleRef.current = parsed;
            const res = await api.importBackup(parsed); // DRY RUN — writes nothing
            toast.dismiss(loadingId);
            setReport({ ...res, fileName: file.name });
        } catch (err) {
            console.error(err);
            toast.dismiss(loadingId);
            toast.error(err?.message?.includes("JSON") ? "Not a valid JSON file" : "Import check failed");
        } finally {
            setBusy(false);
        }
    };

    const applyImport = async () => {
        if (!bundleRef.current) return;
        setBusy(true);
        try {
            const res = await api.importBackup(bundleRef.current, { apply: true });
            const parts = [...Object.entries(res.imported || {}).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k}`),
                ...(Object.keys(res.settings?.changed || {}).length ? [`${Object.keys(res.settings.changed).length} settings`] : [])].join(", ");
            const skipped = Object.values(res.skipped_existing || {}).reduce((a, b) => a + b, 0);
            toast.success(`${parts ? `Imported ${res.total} (${parts})` : "Nothing new to import"}${skipped ? ` · ${skipped} already present` : ""}${res.tasks?.blocked?.length ? ` · ${res.tasks.blocked.length} task${res.tasks.blocked.length === 1 ? "" : "s"} blocked` : ""}`);
            setReport(null); bundleRef.current = null;
            onDataChanged?.();
        } catch (err) {
            console.error(err);
            toast.error("Import failed — nothing partial was hidden; re-run the check");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div className="mb-2 flex items-baseline justify-between">
                <div>
                    <h2 className="text-xl font-brand font-bold text-zinc-100 uppercase tracking-widest">Backup</h2>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                        one-click export &amp; import
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-3 mt-2">
                <button
                    type="button"
                    onClick={doExport}
                    disabled={busy}
                    className="flex items-center gap-3 rounded-lg border border-meye-cyan/50 bg-meye-cyan/5 hover:bg-meye-cyan/10 disabled:opacity-40 p-4 text-left transition-colors group"
                    data-testid="backup-export-btn"
                >
                    <div className="w-11 h-11 rounded-full bg-meye-cyan/20 border border-meye-cyan/40 flex items-center justify-center flex-shrink-0 group-hover:bg-meye-cyan/30">
                        <Download size={18} className="text-meye-cyan" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-brand font-bold text-zinc-100 mb-0.5">Export everything</div>
                        <div className="text-xs text-zinc-400 break-words">
                            Downloads a JSON bundle of every task, note, checklist, glossary term, inventory item, attachment and setting. Save it somewhere safe.
                        </div>
                    </div>
                </button>

                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    className="flex items-center gap-3 rounded-lg border border-meye-fuchsia/50 bg-meye-fuchsia/5 hover:bg-meye-fuchsia/10 disabled:opacity-40 p-4 text-left transition-colors group"
                    data-testid="backup-import-btn"
                >
                    <div className="w-11 h-11 rounded-full bg-meye-fuchsia/20 border border-meye-fuchsia/40 flex items-center justify-center flex-shrink-0 group-hover:bg-meye-fuchsia/30">
                        <Upload size={18} className="text-meye-fuchsia" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-brand font-bold text-zinc-100 mb-0.5">Import from JSON</div>
                        <div className="text-xs text-zinc-400 break-words">
                            Merge a previous backup (or an old-style Firebase export) into your current data. Existing ids are skipped, new ones get added.
                        </div>
                    </div>
                </button>

                <div className="mt-2 flex items-start gap-2 text-[11px] text-zinc-500 font-mono px-1">
                    <Database size={11} className="mt-0.5 flex-shrink-0" />
                    <span className="break-words">
                        Import is a two-step check: a dry run first, then Apply. Nothing is deleted; duplicates are detected by id; recurring tasks with conflicting or non-source history are blocked for review.
                    </span>
                </div>
                {report && <ImportReport report={report} busy={busy} onApply={applyImport} onCancel={() => { setReport(null); bundleRef.current = null; }} />}
            </div>

            <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                onChange={onFilePicked}
                className="hidden"
                data-testid="backup-import-file"
            />
        </>
    );
};

const ImportReport = ({ report, busy, onApply, onCancel }) => {
    const s = report.tasks?.summary || { safe: 0, canonicalized: 0, blocked: 0 };
    const rows = report.tasks?.report || [];
    const bm = report.bookmarks?.summary || null;
    const bmRows = (report.bookmarks?.report || []).filter((r) => r.status !== "NEW");
    const st = report.settings || null;
    const stChanged = Object.entries(st?.changed || {});
    const other = Object.entries(report.other_counts || {}).filter(([k, v]) => v > 0 && k !== "bookmarks").map(([k, v]) => `${v} ${k}`).join(", ");
    const eligible = s.safe + s.canonicalized + Object.values(report.other_counts || {}).reduce((a, b) => a + b, 0) + stChanged.length;
    return (
        <div className="mt-3 rounded-lg border border-meye-border bg-black/30 p-3 space-y-2" data-testid="import-report">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">dry run · {report.fileName}</div>
            <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300" data-testid="import-safe-count">{s.safe} safe</span>
                <span className="px-2 py-0.5 rounded border border-meye-cyan/40 text-meye-cyan" data-testid="import-canon-count">{s.canonicalized} canonicalized</span>
                <span className="px-2 py-0.5 rounded border border-red-500/40 text-red-300" data-testid="import-blocked-count">{s.blocked} blocked</span>
                {other && <span className="px-2 py-0.5 rounded border border-meye-border text-zinc-400">{other}</span>}
            </div>
            {bm && (
                <div className="flex flex-wrap items-center gap-2 text-xs font-mono" data-testid="import-bookmarks-summary">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">bookmarks</span>
                    <span className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300" data-testid="import-bookmarks-new">{bm.new} new</span>
                    <span className="px-2 py-0.5 rounded border border-meye-border text-zinc-400" data-testid="import-bookmarks-duplicate">{bm.duplicate} duplicate · skipped</span>
                    <span className="px-2 py-0.5 rounded border border-amber-500/40 text-amber-300" data-testid="import-bookmarks-conflict">{bm.conflict} conflict · remapped</span>
                    {bm.blocked > 0 && <span className="px-2 py-0.5 rounded border border-red-500/40 text-red-300" data-testid="import-bookmarks-blocked">{bm.blocked} blocked</span>}
                    <span className="text-zinc-500">· you have {bm.destination_before}</span>
                </div>
            )}
            {st && (
                <div className="space-y-1 text-xs font-mono" data-testid="import-settings-summary">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">settings</span>
                        <span className="px-2 py-0.5 rounded border border-emerald-500/40 text-emerald-300" data-testid="import-settings-changed">{stChanged.length} will change</span>
                        <span className="px-2 py-0.5 rounded border border-meye-border text-zinc-400" data-testid="import-settings-unchanged">{(st.unchanged || []).length} unchanged</span>
                        {(st.skipped_nonportable || []).length > 0 && <span className="px-2 py-0.5 rounded border border-meye-border text-zinc-500" data-testid="import-settings-skipped" title={(st.skipped_nonportable || []).join(", ")}>{st.skipped_nonportable.length} device/transient · not imported</span>}
                    </div>
                    {stChanged.map(([k, v]) => (
                        <div key={k} className="text-[11px] text-zinc-400 pl-1" data-testid={`import-setting-${k}`}>
                            <span className="text-zinc-200">{k}</span>: <span className="line-through text-zinc-600">{String(v.from ?? "—")}</span> → <span className="text-emerald-300">{String(v.to)}</span>
                        </div>
                    ))}
                </div>
            )}
            {bmRows.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 text-[11px] font-mono">
                    {bmRows.map((r) => (
                        <div key={r.id} className={`rounded border p-2 ${r.status === "BLOCKED" ? "border-red-500/40 bg-red-500/5" : r.status === "CONFLICT" ? "border-amber-500/40 bg-amber-500/5" : "border-meye-border bg-black/20"}`} data-testid={`import-bookmark-row-${r.id}`}>
                            <div className="text-zinc-200 truncate">{r.title || r.url || r.id}</div>
                            <div className={r.status === "BLOCKED" ? "text-red-300" : r.status === "CONFLICT" ? "text-amber-300" : "text-zinc-500"}>{r.status.toLowerCase()} · {r.reason}</div>
                        </div>
                    ))}
                </div>
            )}
            {rows.filter((r) => r.status !== "SAFE").length > 0 && (
                <div className="max-h-48 overflow-y-auto space-y-1 text-[11px] font-mono">
                    {rows.filter((r) => r.status !== "SAFE").map((r) => (
                        <div key={r.id} className={`rounded border p-2 ${r.status === "BLOCKED" ? "border-red-500/40 bg-red-500/5" : "border-meye-cyan/30 bg-meye-cyan/5"}`} data-testid={`import-row-${r.id}`}>
                            <div className="text-zinc-200 truncate">{r.text || r.id}</div>
                            <div className={r.status === "BLOCKED" ? "text-red-300" : "text-meye-cyan"}>
                                {r.status === "BLOCKED" ? r.reasons.join(" · ") : r.canonicalization.join(" · ")}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} disabled={busy} className="text-xs text-zinc-400 hover:text-white px-3 py-2" data-testid="import-cancel-btn">cancel</button>
                <button type="button" onClick={onApply} disabled={busy || eligible === 0} className="text-xs bg-meye-fuchsia text-black font-brand uppercase tracking-widest px-4 py-2 rounded disabled:opacity-40" data-testid="import-apply-btn">
                    Apply Import{s.blocked ? ` (skips ${s.blocked})` : ""}
                </button>
            </div>
        </div>
    );
};

export default BackupPanel;
